import { useState, useCallback } from "react";
import { ChevronDown } from "lucide-react";

export interface SqlOptions {
  defaultSchema?: string | undefined;
  statementTimeout?: number | undefined;
  explain?: boolean | undefined;
  explainAnalyze?: boolean | undefined;
  explainVerbose?: boolean | undefined;
  explainCosts?: boolean | undefined;
  explainBuffers?: boolean | undefined;
  explainTiming?: boolean | undefined;
  explainFormat?: "text" | "json" | "xml" | "yaml" | undefined;
  readOnly?: boolean | undefined;
}

interface SqlOptionsPaneProps {
  options: SqlOptions;
  onChange: (options: SqlOptions) => void;
  schemaNames?: string[] | undefined;
}

function NumberField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder?: string | undefined;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="w-24 shrink-0 text-xs text-zinc-400">{label}</label>
      <input
        type="number"
        min={0}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder={placeholder ?? "0"}
        className="w-28 rounded border border-zinc-700 bg-surface px-2 py-1 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
      />
    </div>
  );
}

function ToggleField({
  label,
  value,
  onChange,
  description,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
  description?: string | undefined;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="w-24 shrink-0 text-xs text-zinc-400">{label}</label>
      <button
        type="button"
        onClick={() => onChange(value ? undefined : true)}
        className={`relative h-5 w-9 rounded-full transition-colors ${value ? "bg-accent" : "bg-zinc-700"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${value ? "translate-x-4" : ""}`}
        />
      </button>
      {description && <span className="text-[10px] text-zinc-600">{description}</span>}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="w-24 shrink-0 text-xs text-zinc-400">{label}</label>
      <div className="relative">
        <select
          value={value ?? options[0]}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none rounded border border-zinc-700 bg-surface px-2 py-1 pr-6 font-mono text-xs text-zinc-200 focus:border-accent focus:outline-none"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <ChevronDown
          size={10}
          className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-600"
        />
      </div>
    </div>
  );
}

function SchemaField({
  value,
  onChange,
  schemaNames,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  schemaNames: string[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <label className="w-24 shrink-0 text-xs text-zinc-400">Default Schema</label>
      <div className="relative flex-1">
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || undefined)}
          onFocus={() => schemaNames.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="public"
          className="w-full rounded border border-zinc-700 bg-surface px-2 py-1 pr-6 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
        />
        {schemaNames.length > 0 && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setOpen(!open)}
            className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
          >
            <ChevronDown size={10} />
          </button>
        )}
        {open && schemaNames.length > 0 && (
          <div className="absolute left-0 top-full z-50 mt-1 max-h-40 w-full overflow-auto rounded border border-zinc-700 bg-surface shadow-lg">
            {schemaNames.map((name) => (
              <button
                key={name}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                className={`w-full px-2 py-1 text-left font-mono text-xs hover:bg-surface-overlay ${
                  name === value ? "bg-surface-overlay text-zinc-100" : "text-zinc-300"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function SqlOptionsPane({ options, onChange, schemaNames = [] }: SqlOptionsPaneProps) {
  const update = useCallback(
    (patch: Partial<SqlOptions>) => onChange({ ...options, ...patch }),
    [options, onChange],
  );

  const toggleExplain = useCallback(() => {
    if (options.explain) {
      // Turn off EXPLAIN and clear all sub-options
      onChange({
        ...options,
        explain: undefined,
        explainAnalyze: undefined,
        explainVerbose: undefined,
        explainCosts: undefined,
        explainBuffers: undefined,
        explainTiming: undefined,
        explainFormat: undefined,
      });
    } else {
      // Turn on EXPLAIN with sensible defaults
      update({
        explain: true,
        explainAnalyze: true,
        explainCosts: true,
        explainTiming: true,
        explainFormat: "json",
      });
    }
  }, [options, onChange, update]);

  return (
    <div className="flex flex-col gap-3 overflow-auto p-3">
      {/* Default Schema */}
      <SchemaField
        value={options.defaultSchema}
        onChange={(v) => update({ defaultSchema: v })}
        schemaNames={schemaNames}
      />

      {/* Statement Timeout */}
      <NumberField
        label="Timeout (ms)"
        value={options.statementTimeout}
        onChange={(v) => update({ statementTimeout: v })}
        placeholder="60000"
      />

      {/* EXPLAIN */}
      <div className="flex items-center gap-2">
        <label className="w-24 shrink-0 text-xs text-zinc-400">EXPLAIN</label>
        <button
          type="button"
          onClick={toggleExplain}
          className={`relative h-5 w-9 rounded-full transition-colors ${options.explain ? "bg-accent" : "bg-zinc-700"}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${options.explain ? "translate-x-4" : ""}`}
          />
        </button>
      </div>

      {/* EXPLAIN sub-options */}
      {options.explain && (
        <div className="ml-4 flex flex-col gap-2 border-l border-zinc-800 pl-3">
          <ToggleField
            label="ANALYZE"
            value={options.explainAnalyze}
            onChange={(v) => update({ explainAnalyze: v })}
          />
          <ToggleField
            label="VERBOSE"
            value={options.explainVerbose}
            onChange={(v) => update({ explainVerbose: v })}
          />
          <ToggleField
            label="COSTS"
            value={options.explainCosts}
            onChange={(v) => update({ explainCosts: v })}
          />
          <ToggleField
            label="BUFFERS"
            value={options.explainBuffers}
            onChange={(v) => update({ explainBuffers: v })}
          />
          <ToggleField
            label="TIMING"
            value={options.explainTiming}
            onChange={(v) => update({ explainTiming: v })}
          />
          <SelectField
            label="FORMAT"
            value={options.explainFormat}
            onChange={(v) => update({ explainFormat: v as SqlOptions["explainFormat"] })}
            options={["text", "json", "xml", "yaml"]}
          />
        </div>
      )}

      {/* Read Only */}
      <div className="border-t border-zinc-800 pt-2">
        <ToggleField
          label="Read Only"
          value={options.readOnly}
          onChange={(v) => update({ readOnly: v })}
          description="Wraps query in READ ONLY transaction"
        />
      </div>
    </div>
  );
}
